import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { CheckCircle2, ShieldQuestion, X, ArrowRight, Sparkles } from "lucide-react";
import { toast } from "sonner";

/**
 * RegimeCheckBanner — mostra il regime previdenziale INPS rilevato dal profilo utente
 * (ATECO + eventuale cassa) e chiede una conferma esplicita. Una volta confermato,
 * si nasconde. Se cambia l'ATECO o la cassa, il backend resetta `regime_confermato`
 * → il banner ricompare.
 */

const REGIME_COLORS = {
  artigiani: "from-primary/10 to-primary/5 border-primary/30",
  commercianti: "from-primary/10 to-primary/5 border-primary/30",
  gestione_separata: "from-secondary/10 to-secondary/5 border-secondary/30",
  cassa: "from-emerald-100 to-emerald-50 border-emerald-300",
  nessuno: "from-muted/50 to-transparent border-muted",
};

export default function RegimeCheckBanner() {
  const { user, setUser } = useAuth();
  const [info, setInfo] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    // Carica solo se l'utente ha una P.IVA attiva
    if (!user) return;
    if (user.tipo_piva !== "forfettario" && user.tipo_piva !== "ordinario") {
      setInfo(null);
      return;
    }
    if (user.regime_confermato) {
      setInfo(null);
      return;
    }
    api
      .get("/auth/regime-info")
      .then((r) => setInfo(r.data))
      .catch(() => setInfo(null));
  }, [user?.regime_confermato, user?.tipo_piva, user?.codice_ateco, user?.cassa_previdenziale]);

  const confirm = async () => {
    setConfirming(true);
    try {
      const r = await api.patch("/auth/profile", { regime_confermato: true });
      if (setUser) setUser(r.data);
      toast.success("Regime confermato — questo avviso non ricomparirà");
      setInfo(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    } finally {
      setConfirming(false);
    }
  };

  if (dismissed || !info || info.confermato || info.regime === "nessuno") return null;

  const bg = REGIME_COLORS[info.regime] || REGIME_COLORS.gestione_separata;

  return (
    <Card
      className={`p-5 rounded-2xl border-2 bg-gradient-to-br ${bg} fade-in-up`}
      data-testid="regime-check-banner"
    >
      <div className="flex items-start gap-4">
        <div className="w-11 h-11 rounded-2xl bg-background flex items-center justify-center flex-shrink-0 shadow-sm">
          <ShieldQuestion className="w-5 h-5 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              Regime previdenziale rilevato
            </span>
            <Sparkles className="w-3 h-3 text-secondary" />
          </div>
          <div className="font-heading font-bold text-lg leading-tight" data-testid="regime-check-label">
            {info.label}
          </div>
          <div className="text-xs text-muted-foreground mt-1 leading-relaxed">
            {info.description}
            {info.codice_ateco && (
              <span className="block mt-1">
                Basato sul tuo codice ATECO <span className="font-mono font-semibold">{info.codice_ateco}</span>
                {info.cassa_previdenziale ? ` + cassa "${info.cassa_previdenziale}"` : ""}.
              </span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2 mt-3">
            <Button
              onClick={confirm}
              disabled={confirming}
              size="sm"
              className="rounded-full h-9"
              data-testid="regime-check-confirm"
            >
              <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
              {confirming ? "Confermo…" : "Sì, è corretto"}
            </Button>
            <Link to="/app/impostazioni" data-testid="regime-check-edit">
              <Button size="sm" variant="outline" className="rounded-full h-9">
                Correggi in Impostazioni <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
              </Button>
            </Link>
          </div>
        </div>
        <button
          onClick={() => setDismissed(true)}
          className="p-1 rounded-lg hover:bg-background/50 transition-colors flex-shrink-0"
          data-testid="regime-check-dismiss"
          aria-label="Chiudi"
        >
          <X className="w-4 h-4 text-muted-foreground" />
        </button>
      </div>
    </Card>
  );
}
